/**
 * Error normalization.
 *
 * The backend's `detail` field has two shapes:
 *   - hand-raised HTTPException  → a plain string
 *   - FastAPI 422 validation     → an array of { loc, msg, type } objects
 *
 * Rendering `detail` unguarded is how the legacy UI printed "[object Object]".
 * Everything the app displays comes from `ApiError.message`, which is always a string.
 */

export interface FieldError {
  /** Dotted path into the submitted body, e.g. "schedule.role_group_id". */
  readonly field: string;
  readonly message: string;
}

interface ValidationItem {
  loc?: unknown;
  msg?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly fieldErrors: readonly FieldError[];
  readonly payload: unknown;

  constructor(
    message: string,
    status: number,
    options: { fieldErrors?: readonly FieldError[]; payload?: unknown } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fieldErrors = options.fieldErrors ?? [];
    this.payload = options.payload;
  }

  /** The session is gone. Triggers re-authentication. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Authenticated but not allowed. Renders the 403 page, never a silent redirect. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * A job for this project+stage is already running.
   *
   * This is a NORMAL state in this API, not a failure — every stage trigger returns it
   * when re-triggered. Screens should render "already running", not an error toast.
   */
  get isAlreadyRunning(): boolean {
    return this.status === 409;
  }

  get isValidation(): boolean {
    return this.status === 422;
  }

  /** Nothing reached the server — offline, DNS, CORS preflight rejection. */
  get isNetwork(): boolean {
    return this.status === 0;
  }

  /** Message for a specific form field, if the server flagged one. */
  fieldError(field: string): string | undefined {
    return this.fieldErrors.find((e) => e.field === field)?.message;
  }
}

/** "body" and a leading index are noise; "schedule.role_group_id" is what a form needs. */
function locToField(loc: unknown): string {
  if (!Array.isArray(loc)) return '';
  return loc
    .filter((part): part is string | number => typeof part === 'string' || typeof part === 'number')
    .filter((part, index) => !(index === 0 && (part === 'body' || part === 'query' || part === 'path')))
    .join('.');
}

function parseValidationDetail(detail: readonly unknown[]): FieldError[] {
  const errors: FieldError[] = [];
  for (const raw of detail) {
    if (typeof raw !== 'object' || raw === null) continue;
    const item = raw as ValidationItem;
    const message = typeof item.msg === 'string' ? item.msg : 'Invalid value';
    errors.push({ field: locToField(item.loc), message });
  }
  return errors;
}

function describe(status: number): string {
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'That item could not be found.';
  if (status === 409) return 'This is already running.';
  if (status === 429) return 'Too many requests. Please wait a moment and try again.';
  if (status >= 500) return 'The server ran into a problem. Please try again.';
  return 'Something went wrong.';
}

/** Builds an ApiError from a failed response body of any shape. */
export function toApiError(status: number, body: unknown): ApiError {
  if (typeof body !== 'object' || body === null) {
    const message = typeof body === 'string' && body.trim() !== '' ? body : describe(status);
    return new ApiError(message, status, { payload: body });
  }

  const detail = (body as { detail?: unknown }).detail;

  if (typeof detail === 'string' && detail.trim() !== '') {
    return new ApiError(detail, status, { payload: body });
  }

  if (Array.isArray(detail)) {
    const fieldErrors = parseValidationDetail(detail);
    const message =
      fieldErrors.length === 0
        ? describe(status)
        : fieldErrors
            .map((e) => (e.field ? `${e.field}: ${e.message}` : e.message))
            .join('; ');
    return new ApiError(message, status, { fieldErrors, payload: body });
  }

  // Object detail { code, message } (e.g. 503 DATABASE_UNAVAILABLE): show the sentence, never the code.
  if (typeof detail === 'object' && detail !== null && !Array.isArray(detail)) {
    const sentence = (detail as { message?: unknown }).message;
    if (typeof sentence === 'string' && sentence.trim() !== '') {
      return new ApiError(sentence, status, { payload: body });
    }
  }

  // Some routes use `message` or `error` instead of `detail`.
  for (const key of ['message', 'error'] as const) {
    const value = (body as Record<string, unknown>)[key];
    if (typeof value === 'string' && value.trim() !== '') {
      return new ApiError(value, status, { payload: body });
    }
  }

  return new ApiError(describe(status), status, { payload: body });
}

/** Never throws. Use wherever an unknown caught value must become display text. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  if (typeof error === 'string' && error.trim() !== '') return error;
  return 'Something went wrong.';
}
